import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { Facebook, Instagram, MessageCircle, Phone, Send, Calendar as CalIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

const CHANNELS = [
  { id: 'facebook', label: 'Facebook', Icon: Facebook },
  { id: 'instagram', label: 'Instagram', Icon: Instagram },
  { id: 'whatsapp', label: 'WhatsApp', Icon: Phone },
  { id: 'snapchat', label: 'Snapchat', Icon: MessageCircle },
  { id: 'viber', label: 'Viber', Icon: Send },
];

const statusColor = (s) => s === 'Published' ? 'border-emerald-600 text-emerald-400' : s === 'Scheduled' ? 'border-amber-600 text-amber-400' : 'border-zinc-700 text-zinc-400';

const AdminMarketing = () => {
  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState({ title: '', body: '', image: '', channels: [] });
  const [date, setDate] = useState(null);

  const load = () => api.listPosts().then(setPosts).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const toggle = (id) => setDraft((d) => ({ ...d, channels: d.channels.includes(id) ? d.channels.filter((x) => x !== id) : [...d.channels, id] }));

  const publishNow = async () => {
    if (!draft.title) return toast.error('Title required');
    if (draft.channels.length === 0) return toast.error('Pick at least 1 channel');
    try {
      await api.createPost({ ...draft, status: 'Published', scheduled_at: new Date().toISOString().slice(0,16).replace('T',' ') });
      setDraft({ title: '', body: '', image: '', channels: [] });
      toast.success('Post published (share-links generated; connect channel keys for direct posting)');
      load();
    } catch { toast.error('Could not save'); }
  };
  const schedule = async () => {
    if (!date) return toast.error('Pick a date');
    if (!draft.title || draft.channels.length === 0) return toast.error('Title & channels required');
    try {
      await api.createPost({ ...draft, status: 'Scheduled', scheduled_at: date.toISOString().slice(0,16).replace('T',' ') });
      setDraft({ title: '', body: '', image: '', channels: [] });
      setDate(null);
      toast.success('Post scheduled');
      load();
    } catch { toast.error('Could not save'); }
  };
  const del = async (id) => { try { await api.deletePost(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };

  return (
    <div className="space-y-6">
      <p className="text-zinc-400 text-sm">Compose once, publish everywhere. Direct share to <span className="text-[#d4af37]">Facebook, Instagram, WhatsApp, Snapchat, Viber</span>. <span className="text-[#d4af37]">Posts persist to DB.</span></p>

      <Tabs defaultValue="compose">
        <TabsList className="bg-zinc-900">
          <TabsTrigger value="compose">Compose</TabsTrigger>
          <TabsTrigger value="posts">Posts ({posts.length})</TabsTrigger>
          <TabsTrigger value="settings">Channel keys</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-6 grid lg:grid-cols-[1fr,360px] gap-6">
          <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-4">
            <div><Label>Title / Hook</Label><Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="E.g. New Drop: Cherry Berry 20K — Restocked!" className="bg-transparent border-zinc-800 mt-1" /></div>
            <div><Label>Body</Label><Textarea rows={5} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="Write your post once. We adapt it per channel automatically." className="bg-transparent border-zinc-800 mt-1" /></div>
            <div><Label>Image URL</Label><Input value={draft.image} onChange={(e) => setDraft({ ...draft, image: e.target.value })} placeholder="https://…" className="bg-transparent border-zinc-800 mt-1" /></div>
            <div>
              <Label>Channels</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CHANNELS.map(({ id, label, Icon }) => {
                  const active = draft.channels.includes(id);
                  return (
                    <button key={id} type="button" onClick={() => toggle(id)} className={`flex items-center gap-2 px-3 py-2 rounded border text-sm transition-colors ${active ? 'border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]' : 'border-zinc-800 text-zinc-300 hover:border-zinc-700'}`}><Icon className="h-4 w-4" /> {label}</button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button className="btn-gold" onClick={publishNow}>Publish now</Button>
              <Popover><PopoverTrigger asChild><Button variant="outline" className="btn-outline-gold"><CalIcon className="h-4 w-4 mr-2" />{date ? date.toDateString() : 'Pick date'}</Button></PopoverTrigger><PopoverContent className="bg-zinc-950 border-zinc-900 p-0"><Calendar mode="single" selected={date} onSelect={setDate} /></PopoverContent></Popover>
              <Button variant="outline" className="btn-outline-gold" onClick={schedule}>Schedule</Button>
            </div>
          </Card>

          <Card className="bg-zinc-950 border-zinc-900 p-6">
            <h4 className="serif-italic text-[#d4af37] text-xl mb-3">Live preview</h4>
            <div className="border border-zinc-800 rounded overflow-hidden">
              {draft.image && <img src={draft.image} alt="" className="w-full aspect-video object-cover" onError={(e) => { e.currentTarget.style.display='none'; }} />}
              <div className="p-4"><p className="text-zinc-100 font-medium">{draft.title || 'Your post title'}</p><p className="text-zinc-400 text-sm mt-1">{draft.body || 'Your post body will appear here, adapted per channel.'}</p><div className="mt-3 flex flex-wrap gap-1">{draft.channels.map((c) => <Badge key={c} variant="outline" className="border-[#d4af37] text-[#d4af37] text-xs">{c}</Badge>)}</div></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="posts" className="mt-6">
          <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60"><tr className="text-left text-zinc-400"><th className="p-3">Title</th><th className="p-3">Channels</th><th className="p-3">Status</th><th className="p-3">Time</th><th className="p-3"></th></tr></thead>
              <tbody>
                {posts.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-500">No posts yet</td></tr>}
                {posts.map((p) => (
                  <tr key={p.id} className="border-t border-zinc-900">
                    <td className="p-3"><p className="text-zinc-100">{p.title}</p><p className="text-zinc-500 text-xs line-clamp-1 max-w-md">{p.body}</p></td>
                    <td className="p-3"><div className="flex flex-wrap gap-1">{(p.channels||[]).map((c) => <Badge key={c} variant="outline" className="border-zinc-700 text-zinc-300 text-xs">{c}</Badge>)}</div></td>
                    <td className="p-3"><Badge variant="outline" className={statusColor(p.status)}>{p.status}</Badge></td>
                    <td className="p-3 text-zinc-300">{p.scheduled_at || '—'}</td>
                    <td className="p-3 text-right"><Button size="sm" variant="ghost" className="text-zinc-500 hover:text-rose-400 text-xs" onClick={() => del(p.id)}>Delete</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-4 max-w-2xl">
            <p className="text-zinc-400 text-sm">Connect each channel’s API to enable real publishing. Without keys we generate share-links only.</p>
            {CHANNELS.map(({ id, label, Icon }) => (
              <div key={id} className="grid md:grid-cols-[180px,1fr] gap-3 items-center border border-zinc-900 rounded p-3">
                <div className="flex items-center gap-2 text-zinc-200"><Icon className="h-4 w-4 text-[#d4af37]" /> {label}</div>
                <Input placeholder={`Paste ${label} access token / app secret`} className="bg-transparent border-zinc-800" />
              </div>
            ))}
            <Button className="btn-gold" onClick={() => toast.success('Channel credentials saved (UI only — wire to API later)')}>Save credentials</Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminMarketing;
